import { useState, useMemo } from 'react'
import { useApp } from '@/store/AppContext'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { calculatePrice, formatCurrency } from '@/lib/pricing'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PriceRule, RoundingMode } from '@/lib/types'
import { Settings2, ShieldAlert, Calculator } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

export default function Pricing() {
  const { rules, updateRule } = useApp()
  const { profile } = useAuth()
  const { toast } = useToast()
  const [editingRule, setEditingRule] = useState<PriceRule | null>(null)

  const [previewMins, setPreviewMins] = useState(120)
  const [previewHour, setPreviewHour] = useState(19)

  const previewPrice = useMemo(() => {
    if (!editingRule) return 0
    const dummyTicket: any = {
      type: editingRule.type,
      checkInAt: new Date(new Date().setHours(previewHour, 0, 0, 0)),
      checkOutAt: new Date(new Date().setHours(previewHour, previewMins, 0, 0)),
    }
    return calculatePrice(dummyTicket, [editingRule])
  }, [editingRule, previewMins, previewHour])

  if (profile?.role !== 'admin' && profile?.role !== 'ultramaster') {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center space-y-4 animate-fade-in-up">
        <ShieldAlert className="h-16 w-16 text-destructive" />
        <h2 className="text-2xl font-bold">Acesso Negado</h2>
        <p className="text-muted-foreground">
          Apenas administradores podem acessar a tabela de preços.
        </p>
      </div>
    )
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingRule) {
      updateRule(editingRule)
      toast({
        title: 'Sucesso',
        description: 'Regras de preço atualizadas com sucesso!',
      })
      setEditingRule(null)
    }
  }

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Regras de Preço</h1>
        <p className="text-muted-foreground">
          Gerencie os valores, regras de cobrança e horários de pico por tipo de
          veículo.
        </p>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Tipo</TableHead>
                <TableHead>1ª Hora</TableHead>
                <TableHead>Adicional</TableHead>
                <TableHead>Mensalista</TableHead>
                <TableHead>Hora de Pico</TableHead>
                <TableHead className="text-right">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell className="font-medium">{rule.type}</TableCell>
                  <TableCell>{formatCurrency(rule.firstHour)}</TableCell>
                  <TableCell>{formatCurrency(rule.additionalHour)}</TableCell>
                  <TableCell>
                    {rule.monthlyRate ? formatCurrency(rule.monthlyRate) : '-'}
                  </TableCell>
                  <TableCell>
                    {rule.peakMultiplier
                      ? `${rule.peakMultiplier}x (${rule.peakStartHour} as ${rule.peakEndHour})`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingRule({ ...rule })}
                    >
                      <Settings2 className="h-4 w-4 mr-2" /> Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={!!editingRule}
        onOpenChange={(o) => !o && setEditingRule(null)}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Regra: {editingRule?.type}</DialogTitle>
          </DialogHeader>
          {editingRule && (
            <form onSubmit={handleSave} className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor 1ª Hora (R$)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editingRule.firstHour}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        firstHour: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora Adicional (R$)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editingRule.additionalHour}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        additionalHour: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teto Diária (R$)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editingRule.dailyCap}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        dailyCap: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tolerância (min)</Label>
                  <Input
                    type="number"
                    value={editingRule.graceMinutes}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        graceMinutes: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Arredondamento Adicional</Label>
                  <Select
                    value={editingRule.roundingMode || 'ceil'}
                    onValueChange={(v: RoundingMode) =>
                      setEditingRule({ ...editingRule, roundingMode: v })
                    }
                  >
                    <SelectTrigger className="bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ceil">Teto (Fração = 1h)</SelectItem>
                      <SelectItem value="floor">
                        Piso (Ignora fração)
                      </SelectItem>
                      <SelectItem value="round">Aproximação</SelectItem>
                      <SelectItem value="none">Exato (Proporcional)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4 p-4 bg-muted/50 rounded-lg border border-border">
                <div className="col-span-2">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Calculator className="w-4 h-4" /> Regras Avançadas
                  </h4>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Pacote Mensalista (R$)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    placeholder="Valor mensal..."
                    value={editingRule.monthlyRate || ''}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        monthlyRate: Number(e.target.value) || undefined,
                      })
                    }
                  />
                </div>
                <div className="space-y-2 col-span-2 border-t pt-4 border-border border-dashed">
                  <Label>Multiplicador Horário de Pico</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 1.5 (1.5x do valor)"
                    value={editingRule.peakMultiplier || ''}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        peakMultiplier: Number(e.target.value) || undefined,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Início Pico</Label>
                  <Input
                    type="time"
                    value={editingRule.peakStartHour || ''}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        peakStartHour: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fim Pico</Label>
                  <Input
                    type="time"
                    value={editingRule.peakEndHour || ''}
                    onChange={(e) =>
                      setEditingRule({
                        ...editingRule,
                        peakEndHour: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="bg-secondary/10 border border-secondary/20 p-4 rounded-lg mt-4">
                <p className="text-sm font-bold text-secondary mb-3">
                  Simulador de Valor
                </p>
                <div className="flex gap-3 mb-4">
                  <div className="space-y-1 flex-1">
                    <Label className="text-xs">Tempo (min)</Label>
                    <Input
                      type="number"
                      value={previewMins}
                      onChange={(e) => setPreviewMins(Number(e.target.value))}
                      className="h-9 text-sm bg-background"
                    />
                  </div>
                  <div className="space-y-1 flex-1">
                    <Label className="text-xs">Hora Entrada</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={previewHour}
                      onChange={(e) => setPreviewHour(Number(e.target.value))}
                      className="h-9 text-sm bg-background"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-secondary/20 pt-3">
                  <span className="font-medium text-muted-foreground text-sm">
                    Valor Simulado:
                  </span>
                  <span className="text-2xl font-black text-secondary">
                    {formatCurrency(previewPrice)}
                  </span>
                </div>
              </div>

              <Button type="submit" className="w-full mt-4 font-bold h-12">
                Salvar Regra
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
